'use client';

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { 
  Building2, 
  Users, 
  TrendingUp, 
  Shield, 
  Clock, 
  CheckCircle,
  Phone,
  Mail,
  MapPin,
  Globe,
  Award,
  Target,
  Heart
} from "lucide-react"
import Image from "next/image"
import Link from "next/link"

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <Image
                src="https://rgvfkcfnfbtqyjlrvynz.supabase.co/storage/v1/object/public/vendor-assets/vendor-assets/dark-full.png"
                alt="MenuMate Logo"
                width={200}
                height={60}
                className="dark:invert"
                priority
              />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About MenuMate
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Empowering caterers and food businesses with comprehensive digital solutions 
              to streamline operations, manage orders, and grow their business.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Badge variant="secondary" className="px-4 py-2 text-sm">
                <Building2 className="h-4 w-4 mr-2" />
                Registered Company
              </Badge>
              <Badge variant="secondary" className="px-4 py-2 text-sm">
                <Shield className="h-4 w-4 mr-2" />
                Secure Platform
              </Badge>
              <Badge variant="secondary" className="px-4 py-2 text-sm">
                <Users className="h-4 w-4 mr-2" />
                500+ Caterers
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-16">
        <div className="max-w-6xl mx-auto space-y-16">
          
          {/* Company Information */}
          <section>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  What is MenuMate?
                </h2>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    MenuMate is a comprehensive digital platform designed specifically for caterers, 
                    food businesses, and event planners. We provide end-to-end solutions that help 
                    businesses manage their operations efficiently while delivering exceptional 
                    customer experiences.
                  </p>
                  <p>
                    Our platform combines order management, inventory tracking, customer relationship 
                    management, and payment processing into one seamless solution. Whether you&apos;re a 
                    small catering business or a large-scale food operation, MenuMate scales with your needs.
                  </p>
                  <p>
                    Founded with the vision to digitize the food service industry, we understand 
                    the unique challenges faced by caterers and have built our platform to address 
                    these specific pain points.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-6 text-center">
                  <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Growth Focused</h3>
                  <p className="text-sm text-gray-600">Tools to scale your business</p>
                </Card>
                <Card className="p-6 text-center">
                  <Shield className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Secure & Reliable</h3>
                  <p className="text-sm text-gray-600">Enterprise-grade security</p>
                </Card>
                <Card className="p-6 text-center">
                  <Clock className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">24/7 Support</h3>
                  <p className="text-sm text-gray-600">Always here to help</p>
                </Card>
                <Card className="p-6 text-center">
                  <Users className="h-8 w-8 text-orange-600 mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Customer First</h3>
                  <p className="text-sm text-gray-600">Built for your success</p>
                </Card>
              </div>
            </div>
          </section>

          {/* Our Services */}
          <section>
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Our Services & Features
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive solutions designed to streamline your catering business operations
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Order Management
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Digital order processing</li>
                    <li>• Real-time order tracking</li>
                    <li>• Customer communication</li>
                    <li>• Order history & analytics</li>
                    <li>• Automated notifications</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-blue-600" />
                    Business Management
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Inventory management</li>
                    <li>• Menu & pricing control</li>
                    <li>• Staff & team management</li>
                    <li>• Financial reporting</li>
                    <li>• Business analytics</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-purple-600" />
                    Digital Presence
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Custom menumate links</li>
                    <li>• Online ordering system</li>
                    <li>• Payment gateway integration</li>
                    <li>• WhatsApp Business integration</li>
                    <li>• Social media tools</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Who We Serve */}
          <section className="bg-gray-50 rounded-2xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Who We Serve
              </h2>
              <p className="text-xl text-gray-600">
                MenuMate is designed for various food service businesses
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-semibold mb-2">Caterers</h3>
                <p className="text-sm text-gray-600">Wedding, corporate, and event catering services</p>
              </div>
              <div className="text-center">
                <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Building2 className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Restaurants</h3>
                <p className="text-sm text-gray-600">Dine-in and takeaway restaurant businesses</p>
              </div>
              <div className="text-center">
                <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="font-semibold mb-2">Event Planners</h3>
                <p className="text-sm text-gray-600">Professional event planning and management</p>
              </div>
              <div className="text-center">
                <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Target className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="font-semibold mb-2">Food Vendors</h3>
                <p className="text-sm text-gray-600">Street food and specialty food vendors</p>
              </div>
            </div>
          </section>

          {/* Company Details */}
          <section>
            <div className="bg-white border rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                Company Information
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Legal Information
                  </h3>
                  <div className="space-y-3 text-gray-600">
                    <p><strong>Legal Name:</strong> GETBEGA TECH PRIVATE LIMITED</p>
                    <p><strong>CIN:</strong> U62011KA2024PTC194582</p>
                    <p><strong>PAN:</strong> AALCG5845P</p>
                    <p><strong>Registration Date:</strong> November 5, 2024</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Registered Address
                  </h3>
                  <div className="space-y-3 text-gray-600">
                    <p>
                      Donthila House, Dr No:3-246,<br />
                      Kowkrady Village, Nellyadi,<br />
                      Puttur, Dakshina Kannada - 574229,<br />
                      Karnataka, India
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Information */}
          <section>
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Get in Touch
              </h2>
              <p className="text-xl text-gray-600">
                We&apos;re here to help you grow your business
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="text-center p-6">
                <Phone className="h-8 w-8 text-green-600 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Phone Support</h3>
                <p className="text-gray-600 mb-4">Speak with our support team</p>
                <Button variant="outline" asChild>
                  <a href="tel:+918904885993">+91 89048 85993</a>
                </Button>
              </Card>
              
              <Card className="text-center p-6">
                <Mail className="h-8 w-8 text-blue-600 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Email Support</h3>
                <p className="text-gray-600 mb-4">Send us your questions</p>
                <Button variant="outline" asChild>
                  <a href="mailto:support@menumate.in">support@menumate.in</a>
                </Button>
              </Card>
              
              <Card className="text-center p-6">
                <Globe className="h-8 w-8 text-purple-600 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Get Started</h3>
                <p className="text-gray-600 mb-4">Start your free trial today</p>
                <Button asChild>
                  <Link href="/signup">Start Free Trial</Link>
                </Button>
              </Card>
            </div>
          </section>

        </div>
      </div>
    </div>
  )
}